"use client"

// Sistema de autenticação com encriptação e token único
const TOKEN_STORAGE_KEY = "hotel_auth_token"
const TOKEN_EXPIRED_KEY = "hotel_token_expired"
const SESSION_DATA_KEY = "hotel_session_data"
const COMPLETED_ORDERS_KEY = "hotel_completed_orders"
const TOKEN_EXPIRY_MS = 60 * 60 * 1000 // 1 hora de validade

export interface TokenPayload {
  localId: string
  estabelecimentoId?: string
  menuId?: string
  timestamp: number
  expiresAt: number
  sessionId: string
}

export interface SessionData {
  localId: string
  estabelecimentoId?: string
  menuId?: string
  customerAuth: any
  currentOrderId: string | null
  draftItems: any[]
  lastActivity: number
}

export interface CompletedOrder {
  orderId: string
  customerName: string
  total: number
  completedAt: string
  items: any[]
}

/**
 * Função simples de encriptação (Base64 + rotação)
 */
function encryptData(data: string): string {
  // Converter para base64 e aplicar rotação simples
  const base64 = btoa(data)
  const rotated = base64
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0)
      return String.fromCharCode(code + 3) // Rotação de 3 posições
    })
    .join("")

  return btoa(rotated) // Mais uma camada de base64
}

/**
 * Função simples de descriptografia
 */
function decryptData(encryptedData: string): string {
  try {
    // Primeira camada de decodificação
    const firstDecode = atob(encryptedData)

    // Reverter rotação
    const unrotated = firstDecode
      .split("")
      .map((char) => {
        const code = char.charCodeAt(0)
        return String.fromCharCode(code - 3) // Reverter rotação
      })
      .join("")

    // Segunda decodificação
    return atob(unrotated)
  } catch (error) {
    console.error("Erro ao descriptografar dados:", error)
    return ""
  }
}

/**
 * Gera um token encriptado com localId e estabelecimentoId embutidos
 */
export function generateEncryptedToken(localId: string, estabelecimentoId?: string, menuId?: string): string {
  const sessionId = generateUniqueId()

  const payload: TokenPayload = {
    localId,
    estabelecimentoId,
    menuId,
    timestamp: Date.now(),
    expiresAt: Date.now() + TOKEN_EXPIRY_MS,
    sessionId,
  }

  console.log("[Auth] Gerando novo token com payload:", payload)

  // Encriptar o payload completo
  const encryptedPayload = encryptData(JSON.stringify(payload))

  // Criar token final com prefixo e timestamp
  return `hsc_${encryptedPayload}_${Date.now().toString(36)}`
}

/**
 * Valida e decodifica um token encriptado
 */
export function validateEncryptedToken(token: string): TokenPayload | null {
  try {
    if (!token || !token.startsWith("hsc_")) return null

    const parts = token.split("_")
    if (parts.length !== 3) return null

    const encryptedPayload = parts[1]
    const decryptedData = decryptData(encryptedPayload)

    if (!decryptedData) return null

    const payload = JSON.parse(decryptedData) as TokenPayload

    // Validar estrutura do payload
    if (!payload.localId || !payload.timestamp || !payload.expiresAt || !payload.sessionId) {
      return null
    }

    return payload
  } catch (error) {
    console.error("Erro ao validar token:", error)
    return null
  }
}

/**
 * Verifica se um token está expirado
 */
export function isTokenExpired(token: string): boolean {
  const payload = validateEncryptedToken(token)
  if (!payload) return true

  const now = Date.now()
  const isExpired = now > payload.expiresAt

  console.log("[Auth] Verificando expiração:", {
    agora: new Date(now).toLocaleTimeString(),
    expira: new Date(payload.expiresAt).toLocaleTimeString(),
    expirado: isExpired,
    tempoRestante: Math.max(0, payload.expiresAt - now) / 1000 / 60,
  })

  // Se expirado, marcar imediatamente
  if (isExpired) {
    markTokenAsExpired()
  }

  return isExpired
}

/**
 * Extrai o localId de um token válido
 */
export function getLocalIdFromToken(token: string): string | null {
  const payload = validateEncryptedToken(token)
  return payload ? payload.localId : null
}

/**
 * Extrai o estabelecimentoId de um token válido
 */
export function getEstabelecimentoIdFromToken(token: string): string | null {
  const payload = validateEncryptedToken(token)
  return payload ? payload.estabelecimentoId || null : null
}

/**
 * Força uma nova sessão - limpa tudo e recomeça
 */
export function forceNewSession(): void {
  console.log("[Auth] 🔄 Forçando nova sessão - limpando todos os dados...")

  if (typeof window !== "undefined") {
    try {
      // Limpar TODOS os dados relacionados à sessão
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      localStorage.removeItem(TOKEN_EXPIRED_KEY)
      localStorage.removeItem(SESSION_DATA_KEY)

      console.log("[Auth] ✅ Todos os dados da sessão foram limpos")
    } catch (error) {
      console.error("[Auth] Erro ao limpar dados da sessão:", error)
    }
  }
}

/**
 * Detecta se é um novo scan baseado nos parâmetros da URL
 */
export function isNewScan(): boolean {
  if (typeof window === "undefined") return false

  try {
    const params = new URLSearchParams(window.location.search)
    const hasLocalId = params.has("localId")
    const hasEstabelecimentoId = params.has("estabelecimentoId")
    const hasToken = params.has("token")

    // É novo scan se tem localId na URL e não tem token
    const isNew = (hasLocalId || hasEstabelecimentoId) && !hasToken

    console.log("[Auth] Verificando se é novo scan:", {
      hasLocalId,
      hasEstabelecimentoId,
      hasToken,
      isNewScan: isNew,
    })

    return isNew
  } catch (error) {
    console.error("[Auth] Erro ao verificar novo scan:", error)
    return false
  }
}

/**
 * Marca token como expirado no localStorage
 */
export function markTokenAsExpired(): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(TOKEN_EXPIRED_KEY, "true")
    } catch (error) {
      console.error("Erro ao marcar token como expirado:", error)
    }
  }
}

/**
 * Verifica se token foi marcado como expirado
 */
export function isTokenMarkedAsExpired(): boolean {
  if (typeof window !== "undefined") {
    try {
      return localStorage.getItem(TOKEN_EXPIRED_KEY) === "true"
    } catch (error) {
      console.error("Erro ao verificar se token está marcado como expirado:", error)
      return false
    }
  }
  return false
}

/**
 * Remove marca de expiração
 */
export function clearTokenExpiredMark(): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(TOKEN_EXPIRED_KEY)
      console.log("[Auth] ✅ Marca de expiração removida")
    } catch (error) {
      console.error("Erro ao limpar marca de expiração:", error)
    }
  }
}

/**
 * Salva o token no localStorage
 */
export function saveTokenToStorage(token: string): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token)
      clearTokenExpiredMark()
      console.log("[Auth] ✅ Token salvo no localStorage")
    } catch (error) {
      console.error("Erro ao salvar token:", error)
    }
  }
}

/**
 * Recupera o token do localStorage
 */
export function getTokenFromStorage(): string | null {
  if (typeof window !== "undefined") {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY)
    } catch (error) {
      console.error("Erro ao recuperar token:", error)
      return null
    }
  }
  return null
}

/**
 * Remove o token do localStorage
 */
export function removeTokenFromStorage(): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      console.log("[Auth] ✅ Token removido do localStorage")
    } catch (error) {
      console.error("Erro ao remover token:", error)
    }
  }
}

/**
 * Salva dados da sessão
 */
export function saveSessionData(data: SessionData): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(data))
    } catch (error) {
      console.error("Erro ao salvar dados da sessão:", error)
    }
  }
}

/**
 * Recupera dados da sessão
 */
export function getSessionData(): SessionData | null {
  if (typeof window !== "undefined") {
    try {
      const data = localStorage.getItem(SESSION_DATA_KEY)
      return data ? JSON.parse(data) : null
    } catch (error) {
      console.error("Erro ao recuperar dados da sessão:", error)
      return null
    }
  }
  return null
}

/**
 * Remove todos os dados da sessão
 */
export function clearSessionData(): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(SESSION_DATA_KEY)
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      localStorage.removeItem(TOKEN_EXPIRED_KEY)
      console.log("[Auth] ✅ Todos os dados da sessão foram limpos")
    } catch (error) {
      console.error("Erro ao limpar dados da sessão:", error)
    }
  }
}

/**
 * Salva pedido finalizado
 */
export function saveCompletedOrder(order: CompletedOrder): void {
  if (typeof window !== "undefined") {
    try {
      const existing = getCompletedOrders()
      const updated = [...existing, order]

      // Manter apenas os últimos 10 pedidos
      const limited = updated.slice(-10)

      localStorage.setItem(COMPLETED_ORDERS_KEY, JSON.stringify(limited))
      console.log("[Auth] ✅ Pedido finalizado salvo:", order.orderId)
    } catch (error) {
      console.error("Erro ao salvar pedido finalizado:", error)
    }
  }
}

/**
 * Recupera pedidos finalizados
 */
export function getCompletedOrders(): CompletedOrder[] {
  if (typeof window !== "undefined") {
    try {
      const data = localStorage.getItem(COMPLETED_ORDERS_KEY)
      return data ? JSON.parse(data) : []
    } catch (error) {
      console.error("Erro ao recuperar pedidos finalizados:", error)
      return []
    }
  }
  return []
}

/**
 * Verifica se o token atual é válido (não expirado)
 */
export function isValidToken(): boolean {
  const token = getTokenFromStorage()
  if (!token) return false

  return !isTokenExpired(token)
}

/**
 * Obtém o tempo restante do token em segundos
 */
export function getTokenTimeRemaining(): number {
  const token = getTokenFromStorage()
  if (!token) return 0

  const payload = validateEncryptedToken(token)
  if (!payload) return 0

  const remaining = Math.max(0, payload.expiresAt - Date.now())
  return Math.floor(remaining / 1000)
}

/**
 * Redireciona para URL limpa com apenas o token
 */
export function redirectToCleanURL(token: string): void {
  if (typeof window === "undefined") return

  try {
    // Redirecionar para rota limpa com apenas o token
    const cleanUrl = `${window.location.origin}?token=${token}`
    window.history.replaceState({}, "", cleanUrl)
    console.log("[Auth] ✅ Redirecionado para URL limpa:", cleanUrl)
  } catch (error) {
    console.error("Erro ao redirecionar:", error)
  }
}

/**
 * Remove o token da URL
 */
export function removeTokenFromURL(): void {
  if (typeof window === "undefined") return

  try {
    const url = new URL(window.location.href)
    url.searchParams.delete("token")
    url.searchParams.delete("localId")
    url.searchParams.delete("estabelecimentoId")

    window.history.replaceState({}, "", url.toString())
  } catch (error) {
    console.error("Erro ao remover token da URL:", error)
  }
}

/**
 * Gera um ID único simples
 */
export function generateUniqueId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

/**
 * Adiciona função para verificação automática de expiração
 */
export function checkTokenExpirationAndCleanup(): boolean {
  const token = getTokenFromStorage()
  if (!token) return true

  if (isTokenExpired(token)) {
    // Token expirado - limpar tudo
    removeTokenFromStorage()
    clearSessionData()
    markTokenAsExpired()
    return true
  }

  return false
}
